import { useEffect, useRef, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { KB } from "@/constants/testIds/tickets";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  BookOpen, Plus, Eye, PencilSimple, Trash, FilePdf, UploadSimple, FileArrowDown, Sparkle, LinkSimple, ChatCircleDots, PaperPlaneTilt, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { timeAgo } from "@/lib/format";

// Belt-and-suspenders: backend sanitizes with bleach at ingest, but we
// also DOMPurify on the client before rendering. Data-URI images (Scribe
// exports) must be preserved.
const KB_HTML_CONFIG = {
  ADD_TAGS: ["iframe"],
  ADD_ATTR: ["target", "rel", "allowfullscreen", "frameborder", "allow"],
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|data:image\/(?:png|jpe?g|gif|webp|svg\+xml);)/i,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { title: "", category: "General", content: "", system: "" };
const NO_SYSTEM = "General"; // label for articles without a system

export default function KnowledgeBase() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const canEdit = user?.role === "admin" || user?.role === "agent";
  const isAdmin = user?.role === "admin";
  const [articles, setArticles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeSystem, setActiveSystem] = useState("__all__");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const replacePdfInputRef = useRef(null);

  const load = () => api.get("/kb").then((r) => setArticles(r.data));

  // Group articles by their system taxonomy value. Every configured system
  // gets a tab (even if empty) so admins can drop new articles into it.
  // Articles with no system land in "General".
  const configuredSystems = (settings?.systems || []);
  const systemsWithCounts = useMemo(() => {
    const byName = new Map();
    for (const s of configuredSystems) byName.set(s, 0);
    byName.set(NO_SYSTEM, 0);
    for (const a of articles) {
      const key = a.system || NO_SYSTEM;
      byName.set(key, (byName.get(key) || 0) + 1);
    }
    return [...byName.entries()].map(([name, count]) => ({ name, count }));
  }, [configuredSystems, articles]);
  const filteredArticles = useMemo(() => {
    if (activeSystem === "__all__") return articles;
    if (activeSystem === NO_SYSTEM) return articles.filter((a) => !a.system);
    return articles.filter((a) => a.system === activeSystem);
  }, [articles, activeSystem]);

  // --- AI assistant ---
  const [askOpen, setAskOpen] = useState(false);
  const [askQ, setAskQ] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [askResp, setAskResp] = useState(null);
  const submitAsk = async () => {
    const q = askQ.trim();
    if (!q) return;
    setAskBusy(true);
    setAskResp(null);
    try {
      const r = await api.post("/kb/ask", { question: q });
      setAskResp(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assistant hit an error — try again");
    } finally {
      setAskBusy(false);
    }
  };
  const openArticleFromAssistant = async (aid) => {
    try {
      const r = await api.get(`/kb/${aid}`);
      setSelected(r.data);
      setAskOpen(false);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  // Whenever the selected article has a PDF source, fetch the bytes into a
  // blob URL so we can render the PDF inline (browser-native viewer). Revoke
  // the URL when the selection changes to avoid memory leaks.
  useEffect(() => {
    let cancelled = false;
    let currentUrl = "";
    setPdfBlobUrl("");
    setPdfError("");
    if (!selected?.pdf_path) return undefined;
    setPdfLoading(true);
    api.get(`/kb/${selected.id}/pdf`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const blob = new Blob([r.data], { type: "application/pdf" });
        currentUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(currentUrl);
      })
      .catch(async (err) => {
        if (cancelled) return;
        let msg = "Couldn't load PDF";
        const data = err?.response?.data;
        if (data instanceof Blob) {
          try { msg = JSON.parse(await data.text()).detail || msg; } catch { /* ignore */ }
        } else if (data?.detail) {
          msg = data.detail;
        }
        setPdfError(msg);
      })
      .finally(() => { if (!cancelled) setPdfLoading(false); });
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [selected?.id, selected?.pdf_path]);

  const openArticle = async (a) => {
    try {
      const r = await api.get(`/kb/${a.id}`);
      setSelected(r.data);
      // Refresh list so the (possibly incremented) view count is visible.
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open article");
    }
  };

  const submitCreate = async () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error("Fill title and content");
    setBusy(true);
    try {
      // If the user opened New Article while on a specific system tab, default
      // the article's system to that tab so the workflow stays intuitive.
      const payload = { ...form };
      if (!payload.system && activeSystem !== "__all__" && activeSystem !== NO_SYSTEM) {
        payload.system = activeSystem;
      }
      const r = await api.post("/kb", payload);
      setCreateOpen(false);
      setForm(emptyForm);
      await load();
      setSelected(r.data);
      toast.success("Article published");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };

  const openEdit = () => {
    if (!selected) return;
    setForm({ title: selected.title, category: selected.category, content: selected.content, system: selected.system || "" });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!selected) return;
    if (!form.title.trim() || !form.content.trim()) return toast.error("Fill title and content");
    setBusy(true);
    try {
      const r = await api.patch(`/kb/${selected.id}`, form);
      setEditOpen(false);
      setSelected(r.data);
      await load();
      toast.success("Article updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const removeArticle = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.title}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/kb/${selected.id}`);
      setSelected(null);
      await load();
      toast.success("Article deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!selected?.pdf_path) return;
    try {
      const r = await api.get(`/kb/${selected.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      // Give an actionable message for the "old-PDF gone" case
      let msg = "Couldn't open PDF";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try { msg = JSON.parse(await data.text()).detail || msg; } catch { /* ignore */ }
      } else if (data?.detail) {
        msg = data.detail;
      }
      toast.error(msg, { duration: 10000 });
    }
  };

  const onReplacePdf = () => replacePdfInputRef.current?.click();
  const onReplacePdfChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selected) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please pick a .pdf file");
      return;
    }
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/kb/${selected.id}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSelected(r.data);
      await load();
      toast.success("Source PDF replaced — try opening it now");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't replace PDF");
    } finally {
      setReplacing(false);
    }
  };

  // --- Scribe live link ---
  const [scribeOpen, setScribeOpen] = useState(false);
  const [scribeUrl, setScribeUrl] = useState("");
  const [scribeTitle, setScribeTitle] = useState("");
  const [scribeCategory, setScribeCategory] = useState("Guides");
  const [scribeSystem, setScribeSystem] = useState("");
  const [scribeBusy, setScribeBusy] = useState(false);
  const submitScribe = async () => {
    if (!scribeUrl.trim()) { toast.error("Paste your Scribe share URL first"); return; }
    setScribeBusy(true);
    try {
      const fd = new FormData();
      fd.append("url", scribeUrl.trim());
      if (scribeTitle.trim()) fd.append("title", scribeTitle.trim());
      if (scribeCategory.trim()) fd.append("category", scribeCategory.trim());
      // pre-tag with the active system so the article lands on the right tab
      const sys = scribeSystem || (activeSystem !== "__all__" && activeSystem !== NO_SYSTEM ? activeSystem : "");
      if (sys) fd.append("system", sys);
      const r = await api.post("/kb/from-scribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScribeOpen(false);
      setScribeUrl(""); setScribeTitle(""); setScribeCategory("Guides"); setScribeSystem("");
      await load();
      setSelected(r.data);
      toast.success("Live Scribe article created — edits in Scribe now sync automatically");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add that Scribe link");
    } finally {
      setScribeBusy(false);
    }
  };
  // Given whatever the user pasted (either /shared/ or /embed/ form), derive
  // both variants at render time so the iframe uses the embed URL and the
  // "Open in Scribe" link uses the share URL.
  const scribeEmbedUrl = (stored) => {
    if (!stored) return "";
    try {
      const u = new URL(stored);
      if (u.pathname.startsWith("/embed/")) return stored; // already embed — use verbatim
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts.length === 0) return "";
      const rest = parts.slice(1).join("/") || parts[0];
      return `https://scribehow.com/embed/${rest}?skipIntro=true`;
    } catch { return ""; }
  };
  const scribeOpenUrl = (stored) => {
    if (!stored) return "";
    try {
      const u = new URL(stored);
      if (!u.pathname.startsWith("/embed/")) return stored; // /shared/, /viewer/, /page/ — use verbatim
      // convert /embed/X → /shared/X so the outgoing link opens Scribe's normal reader
      const rest = u.pathname.replace(/^\/embed\//, "");
      return `https://scribehow.com/shared/${rest}`;
    } catch { return ""; }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Knowledge base</h1>
          <p className="text-sm text-slate-500 mt-1">Self-serve articles to deflect common tickets.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => { setAskOpen(true); setAskQ(""); setAskResp(null); }}
            variant="outline"
            data-testid="kb-ask-ai-btn"
            className="h-9 gap-1 border-[#4f4c90]/40 bg-[#4f4c90]/5 text-[#4f4c90] hover:bg-[#4f4c90]/10"
            title="Ask a question and the AI will find the right article"
          >
            <ChatCircleDots size={14} weight="duotone" /> Ask the assistant
          </Button>
        {canEdit && (
          <>
            <Button
              onClick={() => setScribeOpen(true)}
              variant="outline"
              data-testid="kb-scribe-btn"
              className="h-9 gap-1 border-slate-300 bg-white hover:bg-slate-50"
              title="Paste a live Scribe share URL — the article stays in sync with Scribe automatically"
            >
              <LinkSimple size={14} weight="duotone" /> Live Scribe
            </Button>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid={KB.newArticleButton} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1">
                  <Plus size={14} /> New article
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 max-w-lg">
                <DialogHeader><DialogTitle className="font-heading">New article</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-700">Title</Label>
                    <Input data-testid="kb-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-[#FFFFFF] border-slate-200" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-700">Category</Label>
                      <Input data-testid="kb-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 bg-[#FFFFFF] border-slate-200" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700">System</Label>
                      <Select value={form.system || "__none__"} onValueChange={(v) => setForm({ ...form, system: v === "__none__" ? "" : v })}>
                        <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="kb-form-system"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          <SelectItem value="__none__">General (no system)</SelectItem>
                          {configuredSystems.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-700">Content (Markdown supported)</Label>
                    <Textarea data-testid="kb-form-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1 bg-[#FFFFFF] border-slate-200 min-h-40" />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid={KB.createSubmit} disabled={busy} onClick={submitCreate} className="bg-[#4f4c90] hover:bg-[#5F5CA5]">
                    {busy ? "Publishing…" : "Publish"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
        </div>
      </div>

      {/* System tabs — one per configured system, plus All + General */}
      <Tabs value={activeSystem} onValueChange={setActiveSystem}>
        <TabsList
          className="bg-white border border-slate-200 h-auto p-1 flex flex-wrap gap-1 justify-start"
          data-testid="kb-system-tabs"
        >
          <TabsTrigger
            value="__all__"
            className="data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 gap-1.5 h-7 px-2.5 text-xs"
            data-testid="kb-system-tab-all"
          >
            All <span className="font-mono text-[10px] text-slate-500">{articles.length}</span>
          </TabsTrigger>
          {systemsWithCounts.map(({ name, count }) => (
            <TabsTrigger
              key={name}
              value={name}
              className="data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 gap-1.5 h-7 px-2.5 text-xs"
              data-testid={`kb-system-tab-${name}`}
            >
              {name === NO_SYSTEM ? "General" : name}{" "}
              <span className="font-mono text-[10px] text-slate-500">{count}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-4">
        <div className="space-y-2">
          {filteredArticles.map((a) => (
            <button
              key={a.id}
              data-testid={KB.articleCard}
              onClick={() => openArticle(a)}
              className={`w-full text-left hairline rounded-lg p-4 bg-white hover:border-[#4f4c90]/30 transition-colors ${selected?.id === a.id ? "border-[#4f4c90]/40" : ""}`}
            >
              <div className="flex items-start gap-3">
                {a.source === "pdf" ? (
                  <FilePdf size={18} weight="duotone" className="text-[#FB923C] mt-0.5" />
                ) : a.source === "scribe" ? (
                  <LinkSimple size={18} weight="duotone" className="text-[#4f4c90] mt-0.5" />
                ) : (
                  <BookOpen size={18} weight="duotone" className="text-[#4f4c90] mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.title}</div>
                  <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 font-mono">
                    <span>{a.category}</span>
                    {a.system && (
                      <>· <span className="chip !text-[#4f4c90] !border-[#4f4c90]/30 bg-[#4f4c90]/10 !text-[10px] !py-0 !px-1.5">{a.system}</span></>
                    )}
                    · <span>{timeAgo(a.updated_at)}</span>
                    <span className="flex items-center gap-1 ml-auto"><Eye size={11} /> {a.views}</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
          {filteredArticles.length === 0 && (
            <div className="text-slate-500 text-sm text-center p-8 hairline rounded-lg">
              {activeSystem === "__all__"
                ? (canEdit ? "No articles yet — click New article or Upload PDF to start" : "No articles yet")
                : `No articles in ${activeSystem === NO_SYSTEM ? "General" : activeSystem} yet${canEdit ? " — add one from this tab and it'll be tagged automatically" : ""}`}
            </div>
          )}
        </div>

        <div className="hairline rounded-lg bg-white p-6 min-h-64">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-500 text-sm">
              <BookOpen size={32} weight="duotone" />
              <div className="mt-2">Select an article to read</div>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">{selected.category}</div>
                  <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">{selected.title}</h2>
                  <div className="text-[11px] text-slate-500 font-mono mt-1 flex items-center gap-3 flex-wrap">
                    <span>{selected.views} view{selected.views === 1 ? "" : "s"}</span>
                    <span>·</span>
                    <span>updated {timeAgo(selected.updated_at)}</span>
                    {selected.source === "pdf" && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1 text-[#B45309]">
                          <FilePdf size={11} weight="fill" /> PDF article
                        </span>
                      </>
                    )}
                    {selected.source === "html" && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1 text-[#4f4c90]">
                          <FilePdf size={11} weight="fill" /> HTML article
                        </span>
                      </>
                    )}
                    {selected.source === "scribe" && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1 text-[#4f4c90]" title="Renders a live Scribe iframe — updates from Scribe show up here automatically">
                          <LinkSimple size={11} weight="fill" /> Live Scribe
                        </span>
                        {selected.scribe_url && (
                          <>
                            <span>·</span>
                            <a
                              href={scribeOpenUrl(selected.scribe_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-500 hover:text-[#4f4c90] underline underline-offset-2"
                              data-testid="kb-scribe-source"
                            >Open in Scribe ↗</a>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <input
                      ref={replacePdfInputRef}
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      onChange={onReplacePdfChosen}
                      data-testid="kb-replace-pdf-input"
                    />
                    {selected.pdf_path && (
                      <button
                        type="button"
                        onClick={downloadPdf}
                        className="text-[11px] text-slate-600 hover:text-[#4f4c90] flex items-center gap-1 border border-slate-200 rounded-md px-2 py-1"
                        data-testid="kb-download-pdf"
                        title="Open source PDF"
                      >
                        <FileArrowDown size={12} /> Source
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={onReplacePdf}
                      disabled={replacing}
                      className="text-[11px] text-slate-600 hover:text-[#4f4c90] flex items-center gap-1 border border-slate-200 rounded-md px-2 py-1 disabled:opacity-50"
                      data-testid="kb-replace-pdf-btn"
                      title={selected.pdf_path ? "Upload a new source PDF (replaces the current one)" : "Attach a source PDF to this article"}
                    >
                      <UploadSimple size={12} /> {selected.pdf_path ? (replacing ? "Replacing…" : "Replace source") : (replacing ? "Uploading…" : "Attach PDF")}
                    </button>
                    <button
                      type="button"
                      onClick={openEdit}
                      className="text-[11px] text-slate-600 hover:text-[#4f4c90] flex items-center gap-1 border border-slate-200 rounded-md px-2 py-1"
                      data-testid="kb-edit-btn"
                    >
                      <PencilSimple size={12} /> Edit
                    </button>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={removeArticle}
                        disabled={busy}
                        className="text-[11px] text-red-500 hover:text-red-600 flex items-center gap-1 border border-red-200 rounded-md px-2 py-1 disabled:opacity-50"
                        data-testid="kb-delete-btn"
                      >
                        <Trash size={12} /> Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
              {selected.scribe_url ? (
                <div className="mt-4" data-testid="kb-scribe-viewer">
                  <iframe
                    src={scribeEmbedUrl(selected.scribe_url)}
                    title={selected.title}
                    className="w-full rounded-md hairline bg-white"
                    style={{ height: "min(calc(100vh - 220px), 900px)" }}
                    allow="fullscreen"
                    loading="lazy"
                  />
                  <div className="text-[11px] text-slate-500 mt-2 font-mono">
                    Live-embedded from Scribe · updates in the source appear here automatically
                  </div>
                </div>
              ) : selected.pdf_path ? (
                <div className="mt-4" data-testid="kb-pdf-viewer">
                  {pdfLoading && <div className="text-sm text-slate-500 py-4">Loading PDF…</div>}
                  {pdfError && (
                    <div className="hairline rounded-md p-3 bg-amber-50 border-amber-200 text-sm text-amber-900">
                      {pdfError} — try <button onClick={onReplacePdf} className="underline font-medium">replacing the source</button>.
                    </div>
                  )}
                  {pdfBlobUrl && (
                    <iframe
                      src={pdfBlobUrl}
                      title={selected.title}
                      className="w-full rounded-md hairline bg-white"
                      style={{ height: "min(calc(100vh - 260px), 900px)" }}
                    />
                  )}
                </div>
              ) : selected.content_html ? (
                <div
                  className="kb-html-content text-sm text-slate-800 mt-4 leading-relaxed"
                  data-testid="kb-html-body"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.content_html, KB_HTML_CONFIG) }}
                />
              ) : (
                <div className="text-sm text-slate-800 whitespace-pre-wrap mt-4 leading-relaxed">{selected.content}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Edit article</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-700">Title</Label>
              <Input data-testid="kb-edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-[#FFFFFF] border-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-700">Category</Label>
                <Input data-testid="kb-edit-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 bg-[#FFFFFF] border-slate-200" />
              </div>
              <div>
                <Label className="text-xs text-slate-700">System</Label>
                <Select value={form.system || "__none__"} onValueChange={(v) => setForm({ ...form, system: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="kb-edit-system"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="__none__">General (no system)</SelectItem>
                    {configuredSystems.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-700">Content (Markdown supported)</Label>
              <Textarea data-testid="kb-edit-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1 bg-[#FFFFFF] border-slate-200 min-h-56" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} className="text-slate-600 hover:text-slate-900">Cancel</Button>
            <Button data-testid="kb-edit-submit" disabled={busy} onClick={submitEdit} className="bg-[#4f4c90] hover:bg-[#5F5CA5]">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Live Scribe URL dialog */}
      <Dialog open={scribeOpen} onOpenChange={setScribeOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Add a live Scribe link</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500 leading-relaxed space-y-1">
              <div>
                In Scribe, click <b>Share</b> and paste any of the following:
              </div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>the <b>public share link</b> (<span className="font-mono text-[11px]">https://scribehow.com/shared/…</span>), OR</li>
                <li>the <b>full embed code</b> Scribe gives you (<span className="font-mono text-[11px]">&lt;iframe src=&quot;…&quot;&gt;</span>) — we&apos;ll extract the URL for you.</li>
              </ul>
              <div className="text-slate-500">
                <b>Important:</b> the Scribe must be set to <b>Public / Anyone with the link</b> for the embed to render. Private workspace scribes will show a 404 inside the frame even though the outgoing link still works.
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-700">Scribe URL or embed code</Label>
              <Textarea
                data-testid="kb-scribe-url"
                value={scribeUrl}
                onChange={(e) => setScribeUrl(e.target.value)}
                placeholder='https://scribehow.com/shared/…   or   <iframe src="…" …></iframe>'
                className="mt-1 bg-white border-slate-200 font-mono text-[12px] min-h-20"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-700">Title <span className="text-slate-400 font-mono text-[10px]">(optional)</span></Label>
                <Input
                  data-testid="kb-scribe-title"
                  value={scribeTitle}
                  onChange={(e) => setScribeTitle(e.target.value)}
                  placeholder="Auto-detected from URL"
                  className="mt-1 bg-white border-slate-200"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-700">Category</Label>
                <Input
                  data-testid="kb-scribe-category"
                  value={scribeCategory}
                  onChange={(e) => setScribeCategory(e.target.value)}
                  className="mt-1 bg-white border-slate-200"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-700">System</Label>
              <Select
                value={scribeSystem || "__none__"}
                onValueChange={(v) => setScribeSystem(v === "__none__" ? "" : v)}
              >
                <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="kb-scribe-system">
                  <SelectValue placeholder="General (no system)" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="__none__">General (no system)</SelectItem>
                  {configuredSystems.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setScribeOpen(false)} className="text-slate-600 hover:text-slate-900">Cancel</Button>
            <Button
              data-testid="kb-scribe-submit"
              disabled={scribeBusy || !scribeUrl.trim()}
              onClick={submitScribe}
              className="bg-[#4f4c90] hover:bg-[#5F5CA5]"
            >
              {scribeBusy ? "Adding…" : "Add Scribe article"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI assistant — slide-over panel */}
      <Sheet open={askOpen} onOpenChange={setAskOpen}>
        <SheetContent side="right" className="bg-white w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2">
              <ChatCircleDots size={18} weight="duotone" className="text-[#4f4c90]" />
              Ask the assistant
            </SheetTitle>
            <div className="text-xs text-slate-500 leading-relaxed">
              Ask a question in plain English. The assistant will scan every knowledge base article and point you to the best match.
            </div>
          </SheetHeader>
          <form
            className="mt-4 flex gap-2"
            onSubmit={(e) => { e.preventDefault(); submitAsk(); }}
          >
            <Input
              data-testid="kb-ask-input"
              value={askQ}
              onChange={(e) => setAskQ(e.target.value)}
              placeholder="e.g. How do I set up a PO in NetSuite?"
              className="bg-white border-slate-200"
              autoFocus
            />
            <Button
              type="submit"
              data-testid="kb-ask-submit"
              disabled={askBusy || !askQ.trim()}
              className="bg-[#4f4c90] hover:bg-[#5F5CA5] shrink-0 gap-1"
            >
              {askBusy ? <>Thinking…</> : <><PaperPlaneTilt size={14} weight="fill" /> Ask</>}
            </Button>
          </form>
          {askBusy && (
            <div className="mt-4 text-xs text-slate-500 flex items-center gap-2" data-testid="kb-ask-loading">
              <Sparkle size={14} weight="fill" className="text-[#4f4c90] animate-pulse" />
              Searching the knowledge base…
            </div>
          )}
          {askResp && !askBusy && (
            <div className="mt-4 space-y-3" data-testid="kb-ask-response">
              <div className="hairline rounded-lg bg-[#4f4c90]/5 border-[#4f4c90]/20 p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <Sparkle size={12} weight="fill" className="text-[#4f4c90]" />
                  <span className="text-[10px] uppercase font-medium tracking-wider text-[#4f4c90]">
                    Assistant
                  </span>
                  {askResp.confidence && (
                    <span className={`ml-auto text-[10px] font-mono ${askResp.confidence === "high" ? "text-emerald-600" : askResp.confidence === "medium" ? "text-amber-600" : "text-slate-500"}`}>
                      {askResp.confidence} confidence
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                  {askResp.answer}
                </div>
              </div>
              {askResp.matches?.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                    Matching articles
                  </div>
                  {askResp.matches.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => openArticleFromAssistant(m.id)}
                      data-testid={`kb-ask-match-${m.id}`}
                      className="w-full text-left hairline rounded-lg p-3 bg-white hover:border-[#4f4c90]/40 transition-colors group"
                    >
                      <div className="flex items-center gap-2">
                        {m.source === "pdf" ? (
                          <FilePdf size={14} weight="duotone" className="text-[#FB923C]" />
                        ) : m.source === "scribe" ? (
                          <LinkSimple size={14} weight="duotone" className="text-[#4f4c90]" />
                        ) : (
                          <BookOpen size={14} weight="duotone" className="text-[#4f4c90]" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-800 truncate group-hover:text-[#4f4c90]">{m.title}</div>
                          <div className="text-[11px] text-slate-500 font-mono truncate">
                            {m.category}{m.system ? ` · ${m.system}` : ""}
                          </div>
                        </div>
                        <ArrowRight size={14} className="text-slate-400 group-hover:text-[#4f4c90] shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {(!askResp.matches || askResp.matches.length === 0) && (
                <div className="text-xs text-slate-500 italic">
                  No matching articles found. Try rephrasing, or {canEdit ? "create a new article for this topic" : "ask an admin to add one"}.
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
